'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { adminUsersApi } from '@/lib/adminApi';
import { type UserProfile } from '@/lib/climbData';
import Badge from '@/components/ui/Badge';
import Icon from '@/components/ui/AppIcon';
import Modal from '@/components/ui/Modal';

// ─── Row mapper ───────────────────────────────────────────────────────────────

function rowToUser(row: any): UserProfile {
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    avatar: row.avatar,
    stravaId: row.strava_id ?? null,
    stravaProfileUrl: row.strava_profile_url,
    role: row.role,
    status: row.status,
    clubVerified: row.club_verified,
    joinedAt: typeof row.joined_at === 'string' ? row.joined_at.substring(0, 10) : String(row.joined_at ?? ''),
    climbsCompleted: row.climbs_completed,
    bestRank: row.best_rank,
    authMethod: row.auth_method,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const statusVariant = (s: string): 'approved' | 'pending' | 'rejected' | 'default' => {
  if (s === 'active') return 'approved';
  if (s === 'pending') return 'pending';
  if (s === 'suspended') return 'rejected';
  return 'default';
};

const statusLabel = (s: string) => {
  if (s === 'active') return 'Attivo';
  if (s === 'pending') return 'In attesa';
  if (s === 'suspended') return 'Sospeso';
  return s;
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AdminUsersTab() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'pending' | 'suspended'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [suspendTarget, setSuspendTarget] = useState<UserProfile | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'warning'; msg: string } | null>(null);

  const showToast = (type: 'success' | 'error' | 'warning', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const { data, error } = await adminUsersApi.getAll();
    if (error) {
      setLoadError(`Errore caricamento: ${error}`);
      setUsers([]);
    } else {
      setUsers((data ?? []).map(rowToUser));
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filtered = users.filter((u) => {
    const matchSearch = search === '' ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || u.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((u) => u.id)));
  };

  const handleApprove = async (userId: string) => {
    setSaving(true);
    const { error } = await adminUsersApi.update(userId, {
      status: 'active',
      club_verified: true,
      updated_at: new Date().toISOString(),
    });
    setSaving(false);
    if (error) {
      showToast('error', `Errore approvazione: ${error}`);
    } else {
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, status: 'active' as const, clubVerified: true } : u));
      showToast('success', 'Utente approvato e verificato');
    }
  };

  const handleSuspend = async (userId: string) => {
    setSaving(true);
    const { error } = await adminUsersApi.update(userId, {
      status: 'suspended',
      updated_at: new Date().toISOString(),
    });
    setSaving(false);
    setSuspendTarget(null);
    if (error) {
      showToast('error', `Errore sospensione: ${error}`);
    } else {
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, status: 'suspended' as const } : u));
      showToast('warning', 'Utente sospeso');
    }
  };

  const handleDelete = async (userId: string) => {
    setSaving(true);
    const { error } = await adminUsersApi.delete(userId);
    setSaving(false);
    setDeleteTarget(null);
    if (error) {
      showToast('error', `Errore eliminazione: ${error}`);
    } else {
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      showToast('success', 'Utente eliminato');
    }
  };

  const handleBulkApprove = async () => {
    setSaving(true);
    const ids = Array.from(selectedIds);
    const { error } = await adminUsersApi.bulkUpdate(ids, {
      status: 'active',
      club_verified: true,
      updated_at: new Date().toISOString(),
    });
    setSaving(false);
    if (error) {
      showToast('error', `Errore approvazione multipla: ${error}`);
    } else {
      setUsers((prev) => prev.map((u) => selectedIds.has(u.id) ? { ...u, status: 'active' as const, clubVerified: true } : u));
      showToast('success', `${ids.length} utenti approvati`);
      setSelectedIds(new Set());
    }
  };

  const toastColor = (type: string) => {
    if (type === 'success') return 'bg-primary/10 border-primary/30 text-primary';
    if (type === 'warning') return 'bg-yellow-400/10 border-yellow-400/30 text-yellow-400';
    return 'bg-red-400/10 border-red-400/30 text-red-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Toast */}
      {toast && (
        <div className={`mb-4 px-4 py-3 rounded-lg border text-sm font-semibold flex items-center gap-2 ${toastColor(toast.type)}`}>
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : toast.type === 'warning' ? 'ExclamationTriangleIcon' : 'ExclamationCircleIcon'} size={16} />
          {toast.msg}
        </div>
      )}

      {/* Load error */}
      {loadError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-400/10 border border-red-400/30 text-red-400 text-sm flex items-center justify-between">
          <span className="flex items-center gap-2"><Icon name="ExclamationTriangleIcon" size={16} />{loadError}</span>
          <button onClick={loadUsers} className="text-xs underline">Riprova</button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Cerca per nome o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-9 pr-4 py-2.5 text-sm w-full rounded-lg"
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'active', 'pending', 'suspended'] as const).map((s) => (
            <button
              key={`filter-${s}`}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all duration-150 ${filterStatus === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'}`}
            >
              {s === 'all' ? 'Tutti' : statusLabel(s)}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-primary/10 border border-primary/30 rounded-xl fade-in">
          <span className="text-sm font-semibold text-primary">{selectedIds.size} selezionati</span>
          <button onClick={handleBulkApprove} disabled={saving} className="btn-primary px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1">
            <Icon name="CheckCircleIcon" size={14} />Approva tutti
          </button>
          <button onClick={() => setSelectedIds(new Set())} className="btn-ghost px-3 py-1.5 rounded-lg text-xs font-semibold">Deseleziona</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleSelectAll} className="w-4 h-4 rounded border-border bg-input accent-primary" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Utente</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Ruolo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Stato</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Club</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Auth</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Salite</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Iscrizione</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-16 text-center">
                    <p className="text-sm text-muted-foreground">Nessun utente trovato</p>
                  </td>
                </tr>
              ) : filtered.map((user) => (
                <tr key={`admin-user-${user.id}`} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3.5">
                    <input type="checkbox" checked={selectedIds.has(user.id)} onChange={() => toggleSelect(user.id)} className="w-4 h-4 rounded border-border bg-input accent-primary" />
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                        {user.avatar}
                      </div>
                      <span className="font-semibold text-foreground whitespace-nowrap">{user.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5"><span className="text-sm text-muted-foreground">{user.email}</span></td>
                  <td className="px-4 py-3.5">
                    <Badge variant={user.role === 'admin' ? 'admin' : 'default'}>
                      {user.role === 'admin' ? 'Admin' : 'Utente'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3.5">
                    <Badge variant={statusVariant(user.status) as 'approved' | 'pending' | 'rejected'}>
                      {statusLabel(user.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3.5">
                    {user.clubVerified ? (
                      <span className="flex items-center gap-1 text-xs text-green-400 font-semibold">
                        <Icon name="CheckCircleIcon" size={14} />Verificato
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-yellow-400 font-semibold">
                        <Icon name="ClockIcon" size={14} />In attesa
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    {user.authMethod === 'strava' ? (
                      <span className="flex items-center gap-1 text-xs text-orange-400 font-semibold">
                        <span className="w-2 h-2 rounded-full bg-orange-400" />Strava
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground font-semibold">
                        <Icon name="EnvelopeIcon" size={12} />Email
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-sm text-foreground">{user.climbsCompleted}/9</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-xs text-muted-foreground">{user.joinedAt}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1">
                      {user.status === 'pending' && (
                        <button onClick={() => handleApprove(user.id)} disabled={saving} className="p-1.5 rounded-lg bg-green-400/10 text-green-400 hover:bg-green-400/20 transition-colors" title="Approva utente">
                          <Icon name="CheckIcon" size={14} />
                        </button>
                      )}
                      {user.status !== 'suspended' && user.role !== 'admin' && (
                        <button onClick={() => setSuspendTarget(user)} className="p-1.5 rounded-lg bg-yellow-400/10 text-yellow-400 hover:bg-yellow-400/20 transition-colors" title="Sospendi utente">
                          <Icon name="NoSymbolIcon" size={14} />
                        </button>
                      )}
                      {user.role !== 'admin' && (
                        <button onClick={() => setDeleteTarget(user)} className="p-1.5 rounded-lg bg-red-400/10 text-red-400 hover:bg-red-400/20 transition-colors" title="Elimina utente">
                          <Icon name="TrashIcon" size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Suspend Modal */}
      <Modal open={!!suspendTarget} onClose={() => setSuspendTarget(null)} title="Sospendi utente">
        <p className="text-sm text-muted-foreground mb-5">
          Stai per sospendere <span className="font-semibold text-foreground">{suspendTarget?.name}</span>. L&apos;utente non potrà accedere finché non viene riattivato.
        </p>
        <div className="flex gap-3 justify-end">
          <button onClick={() => setSuspendTarget(null)} className="btn-ghost px-4 py-2 rounded-lg text-sm font-semibold" disabled={saving}>Annulla</button>
          <button
            onClick={() => suspendTarget && handleSuspend(suspendTarget.id)}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-yellow-400/10 text-yellow-400 hover:bg-yellow-400/20 border border-yellow-400/30 transition-colors flex items-center gap-2"
          >
            {saving && <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />}
            Sospendi utente
          </button>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Elimina utente">
        <p className="text-sm text-muted-foreground mb-5">
          Stai per eliminare <span className="font-semibold text-foreground">{deleteTarget?.name}</span>. Questa azione non può essere annullata.
        </p>
        <div className="flex gap-3 justify-end">
          <button onClick={() => setDeleteTarget(null)} className="btn-ghost px-4 py-2 rounded-lg text-sm font-semibold" disabled={saving}>Annulla</button>
          <button
            onClick={() => deleteTarget && handleDelete(deleteTarget.id)}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-400/10 text-red-400 hover:bg-red-400/20 border border-red-400/30 transition-colors flex items-center gap-2"
          >
            {saving && <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />}
            Elimina definitivamente
          </button>
        </div>
      </Modal>
    </div>
  );
}